import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, Empty, Button, Checkbox, Tag, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  getFarmsForScopeApi,
  getPlotsForScopeApi,
  getCropsForScopeApi,
} from '../../api/subusers';

const BLUE = '#1677ff';
const GREEN = '#1a6b2a';

const ck = (farmId, plotId) => `${farmId}_${plotId}`;

function buildScope(farmSels, plotSels, cropSels) {
  const scope = [];
  for (const farmId of farmSels) {
    const fPlots = plotSels.get(farmId);
    const fCrops = cropSels.get(farmId);
    const hasPlots = fPlots?.size > 0;
    const hasCrops = fCrops && [...fCrops.values()].some(s => s.size > 0);

    if (!hasPlots && !hasCrops) {
      scope.push({ farm_id: farmId, plots: [] });
      continue;
    }

    const allPlotIds = new Set([
      ...(fPlots || []),
      ...(fCrops ? fCrops.keys() : []),
    ]);
    const plotScopes = [];

    for (const plotId of allPlotIds) {
      if (fPlots?.has(plotId)) {
        plotScopes.push({ plot_id: plotId, crops: [] });
      } else {
        const crops = fCrops?.get(plotId);
        if (crops?.size > 0) plotScopes.push({ plot_id: plotId, crops: [...crops] });
      }
    }

    scope.push({ farm_id: farmId, plots: plotScopes });
  }
  return scope;
}

// ── FarmRow ────────────────────────────────────────────────────────────
function FarmRow({
  farm, selected,
  plots,        // Plot[] enriched with .crops[] and .cropsLoading
  plotsLoading,
  plotSels,     // Set<plotId>
  cropSels,     // Map<plotId, Set<cropId>>
  tags,
  onToggleFarm, onTogglePlot, onToggleCrop, onRemoveTag,
}) {
  return (
    <div style={{
      border: `1.5px solid ${selected ? BLUE : '#d9d9d9'}`,
      borderRadius: 8,
      marginBottom: 10,
      overflow: 'hidden',
      background: selected ? '#f0f7ff' : '#fafafa',
      transition: 'border-color 0.2s, background 0.2s',
    }}>
      {/* Farm header */}
      <div
        style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer' }}
        onClick={onToggleFarm}
      >
        <Checkbox
          checked={selected}
          onChange={e => { e.stopPropagation(); onToggleFarm(); }}
          style={{ marginRight: 10 }}
        />
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{farm.name}</span>
      </div>

      {/* Nested plot + crop list — shown when farm is selected */}
      {selected && (
        <div style={{ borderTop: '1px solid #e8e8e8', padding: '8px 14px 6px' }}>
          {plotsLoading && plots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}><Spin size="small" /></div>
          ) : plots.length === 0 ? (
            <div style={{ color: '#aaa', fontSize: 13, padding: '4px 0' }}>No plots</div>
          ) : (
            plots.map(plot => (
              <div key={plot.id} style={{ marginBottom: 8 }}>
                {/* Plot row */}
                <Checkbox
                  checked={plotSels.has(plot.id)}
                  onChange={() => onTogglePlot(plot.id)}
                  style={{ fontWeight: 500 }}
                >
                  {plot.name}
                </Checkbox>

                {/* Crops nested under plot */}
                <div style={{ paddingLeft: 24, marginTop: 4 }}>
                  {plot.cropsLoading ? (
                    <Spin size="small" />
                  ) : plot.crops.length === 0 ? (
                    <span style={{ color: '#bbb', fontSize: 12 }}>No crops</span>
                  ) : (
                    plot.crops.map(crop => (
                      <div key={crop.id} style={{ marginBottom: 2 }}>
                        <Checkbox
                          checked={cropSels.get(plot.id)?.has(crop.id) || false}
                          onChange={() => onToggleCrop(plot.id, crop.id)}
                        >
                          {crop.crop}
                        </Checkbox>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Tag row */}
      {selected && (
        <div style={{
          borderTop: '1px solid #f0f0f0',
          padding: '6px 14px 8px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
        }}>
          {tags.map((tag, i) => (
            <Tag
              key={i}
              color={tag.type === 'all' ? 'green' : tag.type === 'plot' ? 'blue' : 'orange'}
              closable
              onClose={e => { e.preventDefault(); onRemoveTag(tag); }}
              style={{ margin: 0 }}
            >
              {tag.label}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ScopePickerModal ───────────────────────────────────────────────────
export default function ScopePickerModal({ open, initialScope, onSave, onCancel }) {
  const [farms, setFarms] = useState([]);
  const [farmsLoading, setFarmsLoading] = useState(false);

  const [farmSels, setFarmSels] = useState(() => new Set());
  const [plotSels, setPlotSels] = useState(() => new Map());   // farmId → Set<plotId>
  const [cropSels, setCropSels] = useState(() => new Map());   // farmId → Map<plotId, Set<cropId>>

  const [loadedPlots, setLoadedPlots] = useState(() => new Map()); // farmId → Plot[]
  const [loadingPlots, setLoadingPlots] = useState(() => new Set());
  const [loadedCrops, setLoadedCrops] = useState(() => new Map()); // ck(fid,pid) → Crop[]
  const [loadingCrops, setLoadingCrops] = useState(() => new Set());

  // in-flight refs prevent duplicate concurrent fetches despite stale closures
  const inFlightPlots = useRef(new Set());
  const inFlightCrops = useRef(new Set());

  // ── Reset & initialise on open ──────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    inFlightPlots.current = new Set();
    inFlightCrops.current = new Set();

    setFarms([]);
    setFarmSels(new Set());
    setPlotSels(new Map());
    setCropSels(new Map());
    setLoadedPlots(new Map());
    setLoadingPlots(new Set());
    setLoadedCrops(new Map());
    setLoadingCrops(new Set());

    let cancelled = false;

    (async () => {
      setFarmsLoading(true);
      try {
        const res = await getFarmsForScopeApi();
        if (cancelled) return;
        setFarms(res.data?.farms || []);

        if (initialScope?.length > 0) {
          // Restore selections
          const newFarmSels = new Set();
          const newPlotSels = new Map();
          const newCropSels = new Map();

          for (const fs of initialScope) {
            newFarmSels.add(fs.farm_id);
            if (!fs.plots?.length) continue;
            const plotSet = new Set();
            const cropMap = new Map();
            for (const ps of fs.plots) {
              if (!ps.crops?.length) {
                plotSet.add(ps.plot_id);
              } else {
                cropMap.set(
                  ps.plot_id,
                  new Set(ps.crops.map(c => (typeof c === 'object' ? (c.id ?? c.crop_id) : c)))
                );
              }
            }
            if (plotSet.size > 0) newPlotSels.set(fs.farm_id, plotSet);
            if (cropMap.size > 0) newCropSels.set(fs.farm_id, cropMap);
          }

          if (!cancelled) {
            setFarmSels(newFarmSels);
            setPlotSels(newPlotSels);
            setCropSels(newCropSels);
          }

          // Auto-load all plots + crops for every pre-selected farm
          await Promise.all(
            initialScope.map(async (fs) => {
              if (cancelled) return;
              const plotRes = await getPlotsForScopeApi(fs.farm_id).catch(() => null);
              const plots = plotRes?.data?.plots || [];
              if (!cancelled) setLoadedPlots(p => new Map([...p, [fs.farm_id, plots]]));
              await Promise.all(
                plots.map(async (plot) => {
                  if (cancelled) return;
                  const cropRes = await getCropsForScopeApi(plot.id).catch(() => null);
                  const crops = cropRes?.data?.crops || [];
                  if (!cancelled)
                    setLoadedCrops(p => new Map([...p, [ck(fs.farm_id, plot.id), crops]]));
                })
              );
            })
          );
        }
      } catch {
        if (!cancelled) message.error('Failed to load farms');
      } finally {
        if (!cancelled) setFarmsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loaders ────────────────────────────────────────────────────
  async function loadPlots(farmId) {
    if (loadedPlots.has(farmId) || inFlightPlots.current.has(farmId))
      return loadedPlots.get(farmId) || [];

    inFlightPlots.current.add(farmId);
    setLoadingPlots(p => { const n = new Set(p); n.add(farmId); return n; });
    try {
      const res = await getPlotsForScopeApi(farmId);
      const plots = res.data?.plots || [];
      setLoadedPlots(p => new Map([...p, [farmId, plots]]));
      return plots;
    } catch {
      message.error('Failed to load plots');
      return [];
    } finally {
      inFlightPlots.current.delete(farmId);
      setLoadingPlots(p => { const n = new Set(p); n.delete(farmId); return n; });
    }
  }

  async function loadCrops(farmId, plotId) {
    const key = ck(farmId, plotId);
    if (loadedCrops.has(key) || inFlightCrops.current.has(key))
      return loadedCrops.get(key) || [];

    inFlightCrops.current.add(key);
    setLoadingCrops(p => { const n = new Set(p); n.add(key); return n; });
    try {
      const res = await getCropsForScopeApi(plotId);
      const crops = res.data?.crops || [];
      setLoadedCrops(p => new Map([...p, [key, crops]]));
      return crops;
    } catch {
      message.error('Failed to load crops');
      return [];
    } finally {
      inFlightCrops.current.delete(key);
      setLoadingCrops(p => { const n = new Set(p); n.delete(key); return n; });
    }
  }

  // ── Farm toggle — auto-load plots → crops on select ─────────────────
  async function toggleFarm(farmId) {
    if (farmSels.has(farmId)) {
      setFarmSels(p => { const n = new Set(p); n.delete(farmId); return n; });
      setPlotSels(p => { const n = new Map(p); n.delete(farmId); return n; });
      setCropSels(p => { const n = new Map(p); n.delete(farmId); return n; });
    } else {
      setFarmSels(p => { const n = new Set(p); n.add(farmId); return n; });
      const plots = await loadPlots(farmId);
      await Promise.all(plots.map(plot => loadCrops(farmId, plot.id)));
    }
  }

  function togglePlot(farmId, plotId) {
    setPlotSels(prev => {
      const next = new Map(prev);
      const set = new Set(next.get(farmId) || []);
      if (set.has(plotId)) set.delete(plotId); else set.add(plotId);
      next.set(farmId, set);
      return next;
    });
  }

  function toggleCrop(farmId, plotId, cropId) {
    setCropSels(prev => {
      const next = new Map(prev);
      const farmMap = new Map(next.get(farmId) || []);
      const set = new Set(farmMap.get(plotId) || []);
      if (set.has(cropId)) set.delete(cropId); else set.add(cropId);
      farmMap.set(plotId, set);
      next.set(farmId, farmMap);
      return next;
    });
  }

  // ── Tag computation ─────────────────────────────────────────────────
  function getFarmTags(farmId) {
    const fPlots = plotSels.get(farmId);
    const fCrops = cropSels.get(farmId);
    const hasPlots = fPlots?.size > 0;
    const hasCrops = fCrops && [...fCrops.values()].some(s => s.size > 0);

    if (!hasPlots && !hasCrops) return [{ type: 'all', label: 'All access' }];

    const tags = [];
    if (hasPlots) {
      const plotMap = Object.fromEntries((loadedPlots.get(farmId) || []).map(p => [p.id, p.name]));
      for (const plotId of fPlots)
        tags.push({ type: 'plot', plotId, label: plotMap[plotId] || `Plot ${plotId}` });
    }
    if (hasCrops) {
      for (const [plotId, cropSet] of fCrops) {
        const cropMap = Object.fromEntries(
          (loadedCrops.get(ck(farmId, plotId)) || []).map(c => [c.id, c.crop])
        );
        for (const cropId of cropSet)
          tags.push({ type: 'crop', plotId, cropId, label: cropMap[cropId] || `Crop ${cropId}` });
      }
    }
    return tags;
  }

  function removeTag(farmId, tag) {
    if (tag.type === 'all') toggleFarm(farmId);
    else if (tag.type === 'plot') togglePlot(farmId, tag.plotId);
    else toggleCrop(farmId, tag.plotId, tag.cropId);
  }

  // ── Save ─────────────────────────────────────────────────────────────
  function handleSave() {
    const scope = buildScope(farmSels, plotSels, cropSels);
    if (!scope.length) { message.error('Select at least one farm'); return; }
    onSave(scope);
  }

  const canSave = buildScope(farmSels, plotSels, cropSels).length > 0;

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onCancel}
            size="small"
            style={{ padding: '0 4px' }}
          />
          <span>Assign Access</span>
        </div>
      }
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            disabled={!canSave}
            onClick={handleSave}
            style={{ background: GREEN, borderColor: GREEN }}
          >
            Save Access
          </Button>
        </div>
      }
      width={560}
      destroyOnClose={false}
      closable
    >
      <div style={{
        background: '#f6ffed',
        border: '1px solid #b7eb8f',
        borderRadius: 6,
        padding: '8px 12px',
        marginBottom: 16,
        fontSize: 13,
        color: '#555',
        lineHeight: 1.8,
      }}>
        <strong>Access levels:</strong>
        <div>Farm selected → access to <em>all</em> plots and crops in that farm</div>
        <div>Plot selected → access to that plot and <em>all</em> its crops</div>
        <div>Crop selected → access to only that specific crop</div>
      </div>

      {farmsLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : farms.length === 0 ? (
        <Empty
          image={<span style={{ fontSize: 40 }}>🌾</span>}
          description={<span>No farms found.<br />Ask the owner to add farms first.</span>}
          style={{ margin: '24px 0' }}
        />
      ) : (
        <div style={{ maxHeight: 480, overflowY: 'auto', paddingRight: 2 }}>
          {farms.map(farm => {
            const farmPlots = loadedPlots.get(farm.id) || [];
            const plotsWithCrops = farmPlots.map(plot => ({
              ...plot,
              crops: loadedCrops.get(ck(farm.id, plot.id)) || [],
              cropsLoading: loadingCrops.has(ck(farm.id, plot.id)),
            }));
            return (
              <FarmRow
                key={farm.id}
                farm={farm}
                selected={farmSels.has(farm.id)}
                plots={plotsWithCrops}
                plotsLoading={loadingPlots.has(farm.id)}
                plotSels={plotSels.get(farm.id) || new Set()}
                cropSels={cropSels.get(farm.id) || new Map()}
                tags={farmSels.has(farm.id) ? getFarmTags(farm.id) : []}
                onToggleFarm={() => toggleFarm(farm.id)}
                onTogglePlot={plotId => togglePlot(farm.id, plotId)}
                onToggleCrop={(plotId, cropId) => toggleCrop(farm.id, plotId, cropId)}
                onRemoveTag={tag => removeTag(farm.id, tag)}
              />
            );
          })}
        </div>
      )}
    </Modal>
  );
}
